/**
 * Centralized error handling for the application.
 *
 * This module provides:
 * - API error parsing
 * - User-friendly error messages
 * - Error logging
 * - Toast notifications for errors
 * - Network and timeout error handling
 * - Retry mechanisms
 * - Error boundaries integration
 */

import * as Sentry from '@sentry/react'
import { toast } from '@/hooks/use-toast'
import type { AxiosError } from 'axios'

export interface ApiError {
  message: string
  detail?: string
  status?: number
  field?: string
}

export interface ParsedError {
  title: string
  description: string
  variant: 'destructive' | 'default'
  isRetryable?: boolean
}

export interface ErrorContext {
  component?: string
  action?: string
  userId?: string
  tenantId?: string
  [key: string]: any
}

/**
 * Context returned by the API when a plan limit is exceeded.
 */
export interface PlanLimitContext {
  resource: 'models' | 'predictions' | 'data_sources' | 'users'
  current_count: number
  plan_limit: number
  current_plan: string
}

/**
 * Parse an API error response into a user-friendly format.
 */
export function parseApiError(error: unknown): ParsedError {
  // Default error
  const defaultError: ParsedError = {
    title: 'Something went wrong',
    description: 'An unexpected error occurred. Please try again.',
    variant: 'destructive',
    isRetryable: false,
  }

  // Handle Axios errors
  if (isAxiosError(error)) {
    const axiosError = error as AxiosError
    const response = axiosError.response

    if (response?.data) {
      const data = response.data as any

      // FastAPI validation errors
      if (Array.isArray(data.detail)) {
        const validationErrors = data.detail
          .map((err: any) => {
            const field = err.loc?.join('.') || 'field'
            return `${field}: ${err.msg}`
          })
          .join(', ')

        return {
          title: 'Validation Error',
          description: validationErrors,
          variant: 'destructive',
          isRetryable: false,
        }
      }

      // Standard API error
      if (typeof data.detail === 'string') {
        return {
          title: getErrorTitle(response.status),
          description: data.detail,
          variant: 'destructive',
          isRetryable: isRetryableStatus(response.status),
        }
      }

      // Generic message
      if (data.message) {
        return {
          title: 'Error',
          description: data.message,
          variant: 'destructive',
          isRetryable: false,
        }
      }
    }

    // Network errors - usually retryable
    if (axiosError.code === 'ERR_NETWORK') {
      return {
        title: 'Network Error',
        description: 'Unable to connect to the server. Please check your connection.',
        variant: 'destructive',
        isRetryable: true,
      }
    }

    // Timeout errors - retryable
    if (axiosError.code === 'ECONNABORTED' || axiosError.code === 'ETIMEDOUT') {
      return {
        title: 'Request Timeout',
        description: 'The request took too long. Please try again.',
        variant: 'destructive',
        isRetryable: true,
      }
    }

    // Cancelled requests - usually not an error
    if (axiosError.code === 'ERR_CANCELED') {
      return {
        title: 'Request Cancelled',
        description: 'The request was cancelled.',
        variant: 'default',
        isRetryable: true,
      }
    }

    // Status-specific errors
    if (response?.status) {
      return {
        title: getErrorTitle(response.status),
        description: getErrorDescription(response.status),
        variant: 'destructive',
        isRetryable: isRetryableStatus(response.status),
      }
    }
  }

  // Handle Error objects
  if (error instanceof Error) {
    return {
      title: 'Error',
      description: error.message,
      variant: 'destructive',
      isRetryable: false,
    }
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      title: 'Error',
      description: error,
      variant: 'destructive',
      isRetryable: false,
    }
  }

  return defaultError
}

/**
 * Check if an error is an Axios error.
 */
function isAxiosError(error: unknown): error is AxiosError {
  return error !== null && typeof error === 'object' && 'isAxiosError' in error
}

/**
 * Determine if an HTTP status code represents a retryable error.
 */
function isRetryableStatus(status: number): boolean {
  // 408 Request Timeout
  // 429 Too Many Requests
  // 500 Internal Server Error
  // 502 Bad Gateway
  // 503 Service Unavailable
  // 504 Gateway Timeout
  return [408, 429, 500, 502, 503, 504].includes(status)
}

/**
 * Get a user-friendly title based on HTTP status code.
 */
function getErrorTitle(status: number): string {
  switch (status) {
    case 400:
      return 'Bad Request'
    case 401:
      return 'Unauthorized'
    case 402:
      return 'Plan Limit Reached'
    case 403:
      return 'Forbidden'
    case 404:
      return 'Not Found'
    case 409:
      return 'Conflict'
    case 422:
      return 'Validation Error'
    case 429:
      return 'Too Many Requests'
    case 500:
      return 'Server Error'
    case 502:
      return 'Bad Gateway'
    case 503:
      return 'Service Unavailable'
    default:
      return 'Error'
  }
}

/**
 * Get a user-friendly description based on HTTP status code.
 */
function getErrorDescription(status: number): string {
  switch (status) {
    case 400:
      return 'The request was invalid. Please check your input.'
    case 401:
      return 'You need to log in to access this resource.'
    case 402:
      return 'You have reached a limit on your current plan. Please upgrade to continue.'
    case 403:
      return 'You do not have permission to access this resource.'
    case 404:
      return 'The requested resource was not found.'
    case 409:
      return 'This action conflicts with existing data.'
    case 422:
      return 'Please check your input and try again.'
    case 429:
      return 'Too many requests. Please wait a moment and try again.'
    case 500:
      return 'An internal server error occurred. Our team has been notified.'
    case 502:
      return 'The server is temporarily unavailable. Please try again later.'
    case 503:
      return 'The service is currently unavailable. Please try again later.'
    default:
      return 'An unexpected error occurred. Please try again.'
  }
}

/**
 * Display an error using toast notifications.
 */
export function showError(error: unknown): void {
  const parsed = parseApiError(error)

  toast({
    title: parsed.title,
    description: parsed.description,
    variant: parsed.variant,
  })

  // Log to console in development
  if (import.meta.env.DEV) {
    console.error('[Error Handler]', error)
  }
}

/**
 * Handle demo-specific errors with custom messaging.
 */
export function handleDemoError(error: unknown): void {
  const parsed = parseApiError(error)

  // Customize messages for common demo errors
  if (parsed.description.includes('Maximum reset limit')) {
    toast({
      title: 'Demo Reset Limit Reached',
      description:
        'You have reached the maximum number of demo resets. Please sign up for a full account to continue.',
      variant: 'destructive',
    })
    return
  }

  if (parsed.description.includes('Demo mode is not enabled')) {
    toast({
      title: 'Demo Unavailable',
      description: 'Demo mode is currently disabled on this instance.',
      variant: 'destructive',
    })
    return
  }

  if (parsed.description.includes('session expired') || parsed.description.includes('expired')) {
    toast({
      title: 'Demo Session Expired',
      description: 'Your demo session has expired. Please start a new demo to continue.',
      variant: 'destructive',
    })
    return
  }

  // Default to standard error handling
  showError(error)
}

/**
 * Log errors for debugging (can be extended to send to monitoring service).
 */
export function logError(error: unknown, context?: ErrorContext): void {
  const parsed = parseApiError(error)

  // Console logging with structured data
  const logData = {
    ...parsed,
    context,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    originalError: error,
  }

  // Use appropriate console method based on severity
  if (parsed.variant === 'destructive') {
    console.error('[Error]', logData)
  } else {
    console.warn('[Warning]', logData)
  }

  // In development, also log stack trace
  if (import.meta.env.DEV && error instanceof Error && error.stack) {
    console.error('Stack trace:', error.stack)
  }

  // Send to Sentry error tracking
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.withScope((scope) => {
      // Add error context as tags
      if (context?.component) {
        scope.setTag('component', context.component)
      }
      if (context?.action) {
        scope.setTag('action', context.action)
      }
      if (context?.userId) {
        scope.setTag('userId', context.userId)
      }
      if (context?.tenantId) {
        scope.setTag('tenantId', context.tenantId)
      }

      // Add full context as extra data
      scope.setContext('errorContext', {
        ...context,
        parsed: parsed,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      })

      // Capture the exception
      if (error instanceof Error) {
        Sentry.captureException(error)
      } else {
        Sentry.captureMessage(parsed.description, 'error')
      }
    })
  }
}

/**
 * Format error for display in UI components.
 */
export function formatError(error: unknown): string {
  const parsed = parseApiError(error)
  return `${parsed.title}: ${parsed.description}`
}

/**
 * Extract error message as plain string.
 */
export function getErrorMessage(error: unknown): string {
  const parsed = parseApiError(error)
  return parsed.description
}

/**
 * Retry helper for failed operations with exponential backoff.
 */
export async function retryOperation<T>(
  operation: () => Promise<T>,
  options: {
    maxRetries?: number
    delayMs?: number
    exponentialBackoff?: boolean
    onRetry?: (attempt: number, error: unknown) => void
    shouldRetry?: (error: unknown) => boolean
  } = {}
): Promise<T> {
  const {
    maxRetries = 3,
    delayMs = 1000,
    exponentialBackoff = true,
    onRetry,
    shouldRetry,
  } = options

  let lastError: unknown

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation()
    } catch (error) {
      lastError = error

      // Check if we should retry this error
      const parsed = parseApiError(error)
      const canRetry = shouldRetry ? shouldRetry(error) : parsed.isRetryable

      if (attempt < maxRetries && canRetry) {
        onRetry?.(attempt, error)

        // Calculate delay with exponential backoff
        const delay = exponentialBackoff
          ? delayMs * Math.pow(2, attempt - 1)
          : delayMs * attempt

        await new Promise((resolve) => setTimeout(resolve, delay))
      } else if (!canRetry) {
        // Don't retry if error is not retryable
        throw error
      }
    }
  }

  throw lastError
}

/**
 * Retry operation with toast notifications.
 */
export async function retryWithToast<T>(
  operation: () => Promise<T>,
  options: {
    maxRetries?: number
    successMessage?: string
    errorMessage?: string
  } = {}
): Promise<T> {
  const { maxRetries = 3, successMessage, errorMessage } = options

  try {
    const result = await retryOperation(operation, {
      maxRetries,
      onRetry: (attempt, error) => {
        const parsed = parseApiError(error)
        toast({
          title: 'Retrying...',
          description: `Attempt ${attempt} of ${maxRetries}: ${parsed.description}`,
          variant: 'default',
        })
      },
    })

    if (successMessage) {
      toast({
        title: 'Success',
        description: successMessage,
        variant: 'default',
      })
    }

    return result
  } catch (error) {
    toast({
      title: 'Operation Failed',
      description: errorMessage || getErrorMessage(error),
      variant: 'destructive',
    })
    throw error
  }
}

/**
 * Create a safe async handler that catches and logs errors.
 */
export function safeAsync<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  options: {
    onError?: (error: unknown) => void
    showToast?: boolean
  } = {}
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args)
    } catch (error) {
      logError(error, { function: fn.name, args })

      if (options.showToast) {
        showError(error)
      }

      options.onError?.(error)
    }
  }) as T
}

/**
 * Check if an error is a plan limit error (HTTP 402).
 */
export function isPlanLimitError(error: unknown): boolean {
  return extractPlanLimitContext(error) !== null
}

/**
 * Extract plan limit context from a 402 error response.
 * Returns null if the error is not a plan limit error or lacks context.
 */
export function extractPlanLimitContext(error: unknown): PlanLimitContext | null {
  if (!isAxiosError(error)) {
    return null
  }

  const axiosError = error as AxiosError
  const response = axiosError.response

  if (response?.status !== 402) {
    return null
  }

  const data = response.data as {
    context?: {
      resource?: string
      current_count?: number
      plan_limit?: number
      current_plan?: string
    }
  }

  // Validate required fields
  if (
    !data?.context ||
    typeof data.context.resource !== 'string' ||
    typeof data.context.current_count !== 'number' ||
    typeof data.context.plan_limit !== 'number' ||
    typeof data.context.current_plan !== 'string'
  ) {
    return null
  }

  // Validate resource type
  const validResources = ['models', 'predictions', 'data_sources', 'users']
  if (!validResources.includes(data.context.resource)) {
    return null
  }

  return {
    resource: data.context.resource as PlanLimitContext['resource'],
    current_count: data.context.current_count,
    plan_limit: data.context.plan_limit,
    current_plan: data.context.current_plan,
  }
}
